import { useEffect, useState, lazy, Suspense } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AuthPage } from './components/auth/AuthPage'
import { SupabaseProvider } from './providers/SupabaseProvider'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingSpinner } from './components/LoadingSpinner'
import { Toaster } from './components/ui/sonner'

// Create a client
const queryClient = new QueryClient()

// Lazy load heavy components for better code splitting
const TaskSidekickApp = lazy(() => import('./components/TaskSidekickApp'))
const DataMigration = lazy(() => import('./components/DataMigration'))
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'))
const ExtensionAuthCallback = lazy(() => import('./pages/ExtensionAuthCallback'))
const GoogleAuthCallback = lazy(() => import('./pages/GoogleAuthCallback'))

function AppContent() {
  const { user, loading, signOut } = useAuth()
  const [migrationComplete, setMigrationComplete] = useState(false)

  // Handle OAuth callback route
  if (window.location.pathname === '/oauth/callback') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <OAuthCallback />
      </Suspense>
    )
  }

  // Handle Google Drive OAuth callback route
  if (window.location.pathname === '/auth/google/callback') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <GoogleAuthCallback />
      </Suspense>
    )
  }

  // Handle extension auth callback route
  if (window.location.pathname === '/extension-auth') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <ExtensionAuthCallback />
      </Suspense>
    )
  }

  // Theme is now handled by ThemeToggle component

  // Check if migration was already done (stored in localStorage)
  useEffect(() => {
    if (user) {
      const migrated = localStorage.getItem(`task-sidekick:migrated:${user.id}`)
      if (migrated === 'true') {
        setMigrationComplete(true)
      }
    }
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <AuthPage />
  }

  // Show data migration screen for first-time users
  if (!migrationComplete) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <DataMigration
          userId={user.id}
          onComplete={() => {
            localStorage.setItem(`task-sidekick:migrated:${user.id}`, 'true')
            setMigrationComplete(true)
          }}
        />
      </Suspense>
    )
  }

  const dataProvider = new SupabaseProvider(user.id)

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <TaskSidekickApp
        dataProvider={dataProvider}
        mode="webapp"
        userId={user.id}
        onSignOut={signOut}
      />
    </Suspense>
  )
}

export function WebApp() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
      <Toaster />
    </ErrorBoundary>
  )
}
